import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { ArrowLeft, Download, Share2, Loader2 } from "lucide-react";
import { api, fmtL, fileUrl } from "@/lib/api";
import { Spinner } from "@/components/opl/Common";

const W = 1080, H = 1350;

export const POSTER_W = W;
export const POSTER_H = H;

export function Face({ p }) {
  const initials = p.name.split(" ").map((w) => w[0]).slice(0, 2).join("");
  return p.photo
    ? <img src={fileUrl(p.photo)} alt={p.name} crossOrigin="anonymous" className="w-full h-full object-cover object-top" />
    : <div className="w-full h-full grid place-items-center text-5xl font-extrabold text-[#F5D061]/80 bg-gradient-to-br from-[#172744] to-[#0B1323]">{initials}</div>;
}

function PlayerTile({ p, color }) {
  return (
    <div className="rounded-2xl overflow-hidden bg-[#0E1A36] border border-white/10 flex flex-col" style={{ boxShadow: `inset 0 -3px 0 ${color}` }}>
      <div className="h-[178px] relative"><Face p={p} />
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#0E1A36] to-transparent" /></div>
      <div className="px-3 pb-3 -mt-2 relative">
        <p className="text-[19px] font-bold leading-tight truncate text-white">{p.name}</p>
        <p className="text-[13px] text-slate-400 truncate">{p.role}</p>
        {p.price != null && <p className="text-[20px] font-extrabold text-[#F5D061] mt-1">{fmtL(p.price)}</p>}
      </div>
    </div>
  );
}

function Poster({ t }) {
  const color = t.color || "#D4AF37";
  const total = t.squad.reduce((s, p) => s + (p.price || 0), 0);
  return (
    <div style={{ width: W, height: H, fontFamily: "Jost, sans-serif" }} className="relative overflow-hidden text-white stage-bg p-12 flex flex-col" data-testid="squad-poster">
      <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full blur-3xl opacity-40" style={{ background: color }} />
      <div className="relative flex items-center justify-between">
        <img src="/logo-white.png" alt="Oneiros" className="h-20" />
        <div className="text-right"><p className="text-[15px] uppercase tracking-[0.3em] text-[#F5D061]">OPL Season 6</p><p className="text-[15px] text-slate-300">{t.club_name} · Official squad</p></div>
      </div>
      <div className="relative flex items-center gap-6 mt-8">
        {t.logo ? <img src={fileUrl(t.logo)} alt={t.name} crossOrigin="anonymous" className="h-28 w-28 rounded-2xl object-cover border-2" style={{ borderColor: color }} />
          : <div className="h-28 w-28 rounded-2xl grid place-items-center text-4xl font-extrabold border-2" style={{ borderColor: color, color }}>{t.short_name}</div>}
        <div className="min-w-0">
          <h1 className="text-[64px] font-extrabold leading-none tracking-tight truncate">{t.name}</h1>
          <p className="text-[22px] text-slate-300 mt-2">Owner · {t.owner_name}</p>
        </div>
      </div>
      <div className="relative grid grid-cols-4 gap-4 mt-8 flex-1">{t.squad.slice(0, 12).map((p) => <PlayerTile key={p.id} p={p} color={color} />)}</div>
      <div className="relative mt-6 flex justify-between items-center text-[20px] border-t border-[#D4AF37]/30 pt-5">
        <span>{t.squad.length} players</span>
        {total > 0 && <span>Squad value <b className="text-[#F5D061]">{fmtL(total)}</b></span>}
        <span className="text-slate-400">opl6.cluboneiros.com</span>
      </div>
    </div>
  );
}

export function PosterShell({ file, title, children }) {
  const [busy, setBusy] = useState(false);
  const [scale, setScale] = useState(0.5);
  const ref = useRef(null);
  useEffect(() => {
    const fit = () => setScale(Math.min(1, (window.innerWidth - 32) / W, (window.innerHeight - 140) / H));
    fit(); window.addEventListener("resize", fit); return () => window.removeEventListener("resize", fit);
  }, []);
  const render = () => toPng(ref.current, { width: W, height: H, pixelRatio: 1, cacheBust: true, style: { transform: "none" } });
  const download = async () => {
    setBusy(true);
    try { const a = document.createElement("a"); a.href = await render(); a.download = file; a.click(); } catch { toast.error("Could not create the image, please try again"); }
    setBusy(false);
  };
  const share = async () => {
    setBusy(true);
    try {
      const blob = await (await fetch(await render())).blob();
      const f = new File([blob], file, { type: "image/png" });
      if (navigator.canShare?.({ files: [f] })) await navigator.share({ files: [f], title });
      else { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = file; a.click(); toast.info("Sharing not supported here, image downloaded instead"); }
    } catch { /* share cancelled */ }
    setBusy(false);
  };
  return (
    <div className="min-h-screen bg-[#030712] text-white flex flex-col items-center p-4">
      <div className="w-full max-w-5xl flex items-center justify-between mb-4 gap-2">
        <Link to="/teams" className="flex items-center gap-1 text-sm text-slate-300" data-testid="poster-back-link"><ArrowLeft className="h-4 w-4" />Teams</Link>
        <div className="flex gap-2">
          <button onClick={share} disabled={busy} className="px-4 py-2 rounded-full border border-white/20 text-sm font-semibold flex items-center gap-2" data-testid="poster-share-btn"><Share2 className="h-4 w-4" />Share</button>
          <button onClick={download} disabled={busy} className="px-4 py-2 rounded-full gold-bg text-sm font-bold flex items-center gap-2" data-testid="poster-download-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Download PNG</button>
        </div>
      </div>
      <div style={{ width: W * scale, height: H * scale }} className="shadow-2xl rounded-xl overflow-hidden">
        <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}><div ref={ref}>{children}</div></div>
      </div>
    </div>
  );
}

export default function SquadPoster() {
  const { teamId } = useParams();
  const [t, setT] = useState(null);
  useEffect(() => { api.get("/public/teams").then((r) => setT(r.data.teams.find((x) => x.id === teamId) || false)); }, [teamId]);
  if (t === null) return <Spinner />;
  if (!t) return <p className="p-10 text-center">Team not found</p>;
  return (
    <PosterShell file={`${t.name.replace(/\s+/g, "_")}_OPL6_Squad.png`} title={`${t.name} · OPL Season 6 squad`}>
      <Poster t={t} />
    </PosterShell>
  );
}
